import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { auth } from 'utils/firebase';
import { AdminPrefsDb } from 'models/AdminPrefs';

// 사이드바 NEW 뱃지 상태를 전역에서 공유하기 위한 컨텍스트
// { badges: { pageKey: boolean }, clear(pageKey), refresh() }
const NewBadgesContext = createContext({
	badges: {},
	clear: () => {},
	refresh: () => {},
});

export const NewBadgesProvider = ({ children }) => {
	const [badges, setBadges] = useState({});
	const [uid, setUid] = useState(null);

	const refresh = useCallback(async () => {
		const currentUid = auth.currentUser?.uid;
		if (!currentUid) {
			setBadges({});
			return;
		}
		const result = await AdminPrefsDb.fetchNewBadges(currentUid);
		setBadges(result);
	}, []);

	useEffect(() => {
		const unsub = auth.onAuthStateChanged((user) => {
			setUid(user?.uid || null);
			if (user) refresh();
			else setBadges({});
		});
		return () => unsub();
	}, [refresh]);

	// 특정 페이지 방문 시 뱃지 즉시 끄고 Firestore 도 업데이트
	const clear = useCallback(async (pageKey) => {
		const currentUid = auth.currentUser?.uid;
		if (!currentUid || !pageKey) return;
		setBadges((prev) => ({ ...prev, [pageKey]: false }));
		try {
			await AdminPrefsDb.updateLastVisited(currentUid, pageKey);
		} catch (e) {
			console.warn(`[useNewBadges] updateLastVisited ${pageKey} failed:`, e);
		}
	}, []);

	return (
		<NewBadgesContext.Provider value={{ badges, clear, refresh, uid }}>
			{children}
		</NewBadgesContext.Provider>
	);
};

export const useNewBadges = () => useContext(NewBadgesContext);

// 페이지 mount 시 호출하면 해당 키의 NEW 뱃지를 끄고 lastVisitedAt 갱신
export const useMarkVisited = (pageKey) => {
	const { clear } = useNewBadges();
	useEffect(() => {
		if (pageKey) clear(pageKey);
	}, [pageKey, clear]);
};

// 특정 prefix 로 시작하는 하위 뱃지 중 하나라도 true 인지 — 부모 메뉴에서 자식 탭 NEW 여부 집계용
export const hasAnyBadgeWithPrefix = (badges, prefix) => {
	if (!badges || !prefix) return false;
	for (const k of Object.keys(badges)) {
		if (k.startsWith(prefix) && badges[k]) return true;
	}
	return false;
};
